"use client";

import { useGoogleLogin } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import Image from "next/image";
import { googleAuthorize } from "@/server/actions/auth";
import { GMAIL_SCOPES } from "@/lib/config";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { finalizeLinkedInOnboardingForCurrentUser } from "@/server/actions/linkedinOnboarding";
import { SESSION_KEY_SESSION_TOKEN } from "@/components/linkedin/LinkedInFillCredentialsForm";

interface GoogleLoginButtonProps {
  setErrorMessage: (message: string) => void;
  redirectTo: string;
  scopes?: string[];
  shouldLogin?: boolean;
  onSuccessfulAuth?: (email: string) => void;
  fullWidth?: boolean;
  clientName?: string;
}

const DEFAULT_SCOPES = GMAIL_SCOPES;

export default function GoogleAuthButton(props: GoogleLoginButtonProps) {
  const {
    setErrorMessage,
    redirectTo,
    scopes,
    shouldLogin,
    onSuccessfulAuth,
    clientName,
  } = props;
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const requestScopes = scopes ? scopes.join(" ") : DEFAULT_SCOPES.join(" ");
  const loginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      setLoading(true);
      googleAuthorize(codeResponse.code, shouldLogin, clientName)
        .then(async ({ userEmail }) => {
          if (userEmail) {
            if (onSuccessfulAuth) {
              onSuccessfulAuth(userEmail);
            }

            try {
              await finalizeLinkedInOnboardingForCurrentUser({
                clientName,
              });
            } catch (error) {
              console.error("finalizeLinkedInOnboarding error", error);
              Sentry.captureException(error, {
                extra: {
                  method: "GoogleAuthButton.finalizeLinkedInOnboardingForCurrentUser",
                  clientName,
                },
              });
              setErrorMessage("We couldn't finish connecting LinkedIn. Please try again.");
              setLoading(false);
              return;
            }
            sessionStorage.removeItem(SESSION_KEY_SESSION_TOKEN);
          }
          router.push(redirectTo);
        })
        .catch((error) => {
          console.error({ error });
          const e = error as any;
          setErrorMessage(e.message);
          setLoading(false);
        });
    },
    onError: (error) => {
      console.log(error);
      setErrorMessage(
        error.error_description ||
          error.error ||
          "Oops an error occurred. Please try again",
      );
    },
    flow: "auth-code",
    scope: requestScopes,
  });

  return loading ? (
    <Loader2 className="animate-spin w-10 h-10" />
  ) : (
    <Button
      size={"lg"}
      onClick={() => {
        setErrorMessage("");
        loginWithGoogle();
      }}
      className={cn(props.fullWidth && "w-full")}
    >
      <Image
        src="/images/google.svg"
        alt="Google Logo"
        width="20"
        height="20"
        className="mr-4"
      />
      Sign In with Google
    </Button>
  );
}
